import { useTranslation, Trans } from 'react-i18next'
import { Mountain } from 'lucide-react'
import WizardModal, { inputCls, selectCls, labelCls } from './WizardModal'
import ImageCropField, { uploadImage } from './ImageCropField'
import { useWS } from '../contexts/WSContext'
import { ORE_TYPES } from '../equipment'
import { apiError } from '../lib/api'

// Wizard de cadastro de Mina: 1) dados (nome, cliente, minérios, coordenada do
// centro), 2) imagem aérea com recorte. O upload da imagem acontece no Concluir.
export default function MineWizard({ open, onClose, editing = null }) {
  const { t } = useTranslation()
  const { clients, reloadAdmin } = useWS()

  const initial = {
    name: editing?.name || '',
    clientId: editing?.client_id ? String(editing.client_id) : (clients[0] ? String(clients[0].id) : ''),
    oreTypes: Array.isArray(editing?.ore_types) && editing.ore_types.length
      ? editing.ore_types
      : (editing?.ore_type ? [editing.ore_type] : []),
    lat: editing?.lat ?? '',
    lon: editing?.lon ?? '',
    language: editing?.language || 'en',
    timezone: editing?.timezone || 'UTC',
    image: editing?.aerial_image_id
      ? { existingId: editing.aerial_image_id, previewUrl: `/api/images/${editing.aerial_image_id}` }
      : null,
  }

  const validCoord = (v, min, max) => v !== '' && !Number.isNaN(Number(v)) && Number(v) >= min && Number(v) <= max

  const steps = [
    {
      title: t('admin:mines.wizard.step_data'),
      valid: f => f.name.trim() !== '' && f.clientId !== '' && validCoord(f.lat, -90, 90) && validCoord(f.lon, -180, 180),
      render: (f, set) => (
        <div className="space-y-4">
          <label className="block"><span className={labelCls}>{t('admin:mines.wizard.name')}</span>
            <input value={f.name} onChange={e => set({ ...f, name: e.target.value })} placeholder={t('admin:mines.wizard.name_ph')} className={inputCls} autoFocus />
          </label>
          <label className="block"><span className={labelCls}>{t('common:entities.client')}</span>
            {clients.length === 0 ? (
              <p className="mt-1 text-xs text-status-stop bg-status-stop/10 rounded-md px-3 py-2">
                <Trans i18nKey="admin:mines.wizard.no_clients" components={[<strong key="0" />]} />
              </p>
            ) : (
              <select value={f.clientId} onChange={e => set({ ...f, clientId: e.target.value })} className={selectCls}>
                <option value="" disabled>{t('admin:select.client')}</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>{t('admin:mines.wizard.language')}</span>
              <select value={f.language} onChange={e => set({ ...f, language: e.target.value })} className={selectCls}>
                <option value="en">{t('admin:mines.wizard.lang_en')}</option>
                <option value="pt">{t('admin:mines.wizard.lang_pt')}</option>
                <option value="fr">{t('admin:mines.wizard.lang_fr')}</option>
                <option value="es">{t('admin:mines.wizard.lang_es')}</option>
                <option value="hy">{t('admin:mines.wizard.lang_hy')}</option>
              </select>
            </label>
            <label className="block"><span className={labelCls}>{t('admin:mines.wizard.timezone')}</span>
              <select value={f.timezone} onChange={e => set({ ...f, timezone: e.target.value })} className={selectCls}>
                <option value="UTC">UTC</option>
                <option value="Africa/Abidjan">Africa/Abidjan</option>
                <option value="Asia/Yerevan">Asia/Yerevan</option>
                <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                <option value="Europe/Lisbon">Europe/Lisbon</option>
              </select>
            </label>
          </div>
          <div>
            <span className={labelCls}>{t('admin:mines.wizard.ore_types')}</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {ORE_TYPES.map(o => {
                const on = f.oreTypes.includes(o)
                return (
                  <button type="button" key={o}
                    onClick={() => set({ ...f, oreTypes: on ? f.oreTypes.filter(x => x !== o) : [...f.oreTypes, o] })}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-brand text-white border-brand' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-brand/50'}`}>
                    {o}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <span className={labelCls}>{t('admin:mines.wizard.center_coord')}</span>
            <div className="mt-1 grid grid-cols-2 gap-3">
              <input value={f.lat} onChange={e => set({ ...f, lat: e.target.value })} placeholder={t('admin:mines.wizard.lat_ph')} inputMode="decimal" className={inputCls + ' mt-0 tabular-nums'} />
              <input value={f.lon} onChange={e => set({ ...f, lon: e.target.value })} placeholder={t('admin:mines.wizard.lon_ph')} inputMode="decimal" className={inputCls + ' mt-0 tabular-nums'} />
            </div>
            {(f.lat !== '' || f.lon !== '') && !(validCoord(f.lat, -90, 90) && validCoord(f.lon, -180, 180)) && (
              <p className="mt-1 text-[11px] text-status-down">{t('admin:mines.wizard.coord_error')}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('admin:mines.wizard.step_image'),
      valid: () => true,
      render: (f, set) => (
        <div className="space-y-2">
          <span className={labelCls}>{t('admin:mines.wizard.aerial_label')}</span>
          <ImageCropField
            value={f.image}
            onChange={img => set({ ...f, image: img })}
            aspect={16 / 9}
            hint={t('admin:mines.wizard.aerial_hint')}
          />
        </div>
      ),
    },
  ]

  const finish = async (f) => {
    let imageId = f.image?.existingId ?? null
    if (f.image?.blob) {
      const up = await uploadImage(f.image, 'mine_aerial')
      imageId = up.id
    }
    const body = {
      name: f.name.trim(),
      client_id: Number(f.clientId),
      ore_type: f.oreTypes[0] || '',
      ore_types: f.oreTypes,
      lat: Number(f.lat),
      lon: Number(f.lon),
      language: f.language,
      timezone: f.timezone,
      aerial_image_id: imageId,
      aerial_image: editing?.aerial_image || '',
      equipment_ids: editing?.equipment_ids || [],
    }
    const url = editing ? `/api/mines/${editing.id}` : '/api/mines'
    const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    if (!res.ok) throw await apiError(res)
    reloadAdmin()
  }

  return (
    <WizardModal
      open={open}
      onClose={onClose}
      title={editing ? t('admin:mines.edit') : t('admin:mines.add')}
      icon={<Mountain size={18} className="text-brand" />}
      steps={steps}
      initial={initial}
      onFinish={finish}
      finishLabel={editing ? t('common:actions.save') : t('admin:actions.register')}
    />
  )
}
